/**
 * Centralised name-privacy contract for team members (Fase 19i).
 *
 * Every surface that renders names, handles or contact info MUST go
 * through `memberDisplay` (or `useMemberDisplay`).
 *
 * | Viewer            | name | fullName | handle | photo |
 * | ----------------- | ---- | -------- | ------ | ----- |
 * | Self / Admin      | yes  | yes      | yes    | yes   |
 * | Non-admin → other | yes  | NO       | NO     | yes   |
 *
 * `scripts/check-name-leaks.js` greps for direct `.last_name` /
 * `.ig_username` accesses outside the allowlist — run it if you suspect
 * a regression.
 */

import type { AccountStatus, UserRole } from '@/types/auth';
import { isInactiveAccountStatus } from './account-status';

export type MemberDisplayInput = {
  id: string;
  first_name?: string;
  last_name?: string;
  ig_username?: string;
  personal_ig_username?: string;
  photo?: string;
  has_used_redemption?: boolean;
  redemption_passed_at?: string;
  account_status?: AccountStatus;
};

export type ViewerContext = {
  viewerId: string;
  viewerRole: UserRole;
};

export type MemberDisplay = {
  /** Always safe to render to any viewer. */
  name: string;
  /** Only present when viewer is self or admin. */
  fullName?: string;
  /** Only present when viewer is self or admin. */
  handle?: string;
  /** True when `handle` falls back to `personal_ig_username` because the
   * work `ig_username` is missing. UI can surface a `[Personal]` badge. */
  personalHandle?: boolean;
  photo?: string;
  /** Fase 28: render the healed-heart scar next to the name. Visible to
   * everyone — the scar is a public mark, not protected info. */
  redemptionScar?: boolean;
  /** Raw account status, when present on the input. Consumers can use it
   * to pick a label (e.g. "Game over" vs "Archivado"). */
  accountStatus?: AccountStatus;
  /** True when the member is banned, failed or deleted — surfaces should
   * mark them as inactive (strikethrough, muted suffix). */
  isInactive?: boolean;
};

function hasScar(input: MemberDisplayInput): boolean {
  return !!input.has_used_redemption && !!input.redemption_passed_at;
}

function applyOptional(result: MemberDisplay, input: MemberDisplayInput): void {
  if (input.photo) result.photo = input.photo;
  if (hasScar(input)) result.redemptionScar = true;
  if (input.account_status) result.accountStatus = input.account_status;
  if (isInactiveAccountStatus(input.account_status)) result.isInactive = true;
}

export function memberDisplay(
  input: MemberDisplayInput,
  viewer: ViewerContext
): MemberDisplay {
  const canSeeFull =
    input.id === viewer.viewerId || viewer.viewerRole === 'admin';
  const firstName = input.first_name?.trim() || 'Sin nombre';
  const lastName = input.last_name?.trim() ?? '';
  const result: MemberDisplay = { name: firstName };
  if (canSeeFull) {
    result.fullName = `${firstName} ${lastName}`.trim();
    if (input.ig_username) {
      result.handle = input.ig_username;
    } else if (input.personal_ig_username) {
      result.handle = input.personal_ig_username;
      result.personalHandle = true;
    }
  }
  applyOptional(result, input);
  return result;
}
